// Match Day — small shared UI pieces: big touch targets (min 48px), no hover-only affordances.
import React, { useEffect, useRef, useState } from 'react';

export const C = {
  green: '#10b981', red: '#ef4444', amber: '#f59e0b', blue: '#3b82f6', purple: '#7c3aed',
};

export function Btn({ children, onClick, tone = 'default', disabled, big, style, title }) {
  const tones = {
    default: { background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-strong)' },
    primary: { background: C.blue, color: '#fff', border: `1px solid ${C.blue}` },
    green: { background: C.green, color: '#fff', border: `1px solid ${C.green}` },
    red: { background: C.red, color: '#fff', border: `1px solid ${C.red}` },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)' },
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      style={{
        ...tones[tone], minHeight: big ? 64 : 48, padding: big ? '0 28px' : '0 16px', borderRadius: 12,
        fontSize: big ? 20 : 15, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1, touchAction: 'manipulation', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', gap: 8, whiteSpace: 'nowrap', ...style,
      }}>
      {children}
    </button>
  );
}

// A destructive action without browser dialogs: first tap arms it ("Tap again to confirm", red),
// the second tap within 4 s runs it. window.confirm() can be silently switched off by the browser
// after a few dialogs ("block dialogs from this site"), which made Undo do nothing on the iPad.
export function ConfirmBtn({ children, confirmLabel = 'Tap again to confirm', onConfirm, disabled, tone = 'ghost', style }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Btn tone={armed ? 'red' : tone} disabled={disabled} style={style}
      onClick={() => { if (armed) { setArmed(false); onConfirm(); } else setArmed(true); }}>
      {armed ? confirmLabel : children}
    </Btn>
  );
}

export function Badge({ children, color = 'var(--text-muted)', solid, style }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 12,
      fontWeight: 700, border: `1px solid ${color}`, color: solid ? '#fff' : color, background: solid ? color : 'transparent',
      whiteSpace: 'nowrap', ...style,
    }}>{children}</span>
  );
}

// Re-renders every second for the live clock — but never while an input has focus, so typing on
// the iPad keyboard is never interrupted.
export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => {
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      setNow(Date.now());
    }, intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// Keeps the screen on while the page is open, where the browser allows it (re-acquired when the
// page becomes visible again — the lock is released whenever it is hidden).
export function useWakeLock() {
  const [active, setActive] = useState(false);
  useEffect(() => {
    let lock = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        if (!navigator.wakeLock || document.visibilityState !== 'visible') return;
        lock = await navigator.wakeLock.request('screen');
        if (cancelled) { lock.release().catch(() => {}); return; }
        setActive(true);
        lock.addEventListener('release', () => setActive(false));
      } catch { setActive(false); }
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, []);
  return active;
}

// A metres field for the iPad keyboard (decimal comma allowed). Keeps its own draft while focused
// and only reports on blur / Enter, so the 1-second refresh can't eat keystrokes.
export function MetresInput({ value, onCommit, placeholder, width = 96, ariaLabel }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value).replace('.', ','));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setDraft(value == null ? '' : String(value).replace('.', ','));
  }, [value]);
  const commit = () => { focused.current = false; onCommit(draft); };
  return (
    <input type="text" inputMode="decimal" value={draft} placeholder={placeholder} aria-label={ariaLabel}
      onFocus={() => { focused.current = true; }}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      style={{
        width, minHeight: 44, padding: '0 10px', borderRadius: 10, fontSize: 17, fontWeight: 600,
        border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)',
        fontVariantNumeric: 'tabular-nums',
      }} />
  );
}

export const fmtTime = iso => (iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '—');
export const fmtDate = ymd => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
export const fmtKickoff = iso => (iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', hour12: false }) : 'time TBC');
